import React, { useState, useEffect } from 'react';

export default function NextModal({ open, onClose, node, onSave }) {
  const [formData, setFormData] = useState({
    nextAction: 'continue', // 'continue', 'redirect', 'end'
    targetNode: '',
    message: '',
    delay: 0
  });

  useEffect(() => {
    if (open && node) {
      setFormData({
        nextAction: node.data.nextAction || 'continue',
        targetNode: node.data.targetNode || '',
        message: node.data.message || '',
        delay: node.data.delay || 0
      });
    }
  }, [open, node]);

  const handleSave = () => {
    if (onSave) {
      onSave(node.id, formData);
    }
    onClose();
  };

  if (!open) return null;

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const contentStyle = {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '500px',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none'
  };

  const textareaStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none',
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit'
  };

  const buttonStyle = {
    background: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    marginRight: '8px'
  };

  const secondaryButtonStyle = {
    background: '#6c757d',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer'
  };

  const radioButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    cursor: 'pointer'
  };

  const radioCircleStyle = {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  };

  const selectedRadioStyle = {
    ...radioCircleStyle,
    border: '2px solid #007bff',
    background: '#007bff'
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#495057', fontSize: '20px', fontWeight: '600' }}>
            Configure Next Action
          </h2>
        </div>

        {/* Next Action Type Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Next Action Type
          </label>
          
          <div
            style={radioButtonStyle}
            onClick={() => setFormData(prev => ({ ...prev, nextAction: 'continue' }))}
          >
            <div style={formData.nextAction === 'continue' ? selectedRadioStyle : radioCircleStyle}>
              {formData.nextAction === 'continue' && <div style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%' }} />}
            </div>
            <span style={{ fontSize: '16px' }}>➡️</span>
            <span style={{ fontSize: '14px', color: '#495057' }}>Continue to Next Node</span>
          </div>
          
          <div
            style={radioButtonStyle}
            onClick={() => setFormData(prev => ({ ...prev, nextAction: 'redirect' }))}
          >
            <div style={formData.nextAction === 'redirect' ? selectedRadioStyle : radioCircleStyle}>
              {formData.nextAction === 'redirect' && <div style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%' }} />}
            </div>
            <span style={{ fontSize: '16px' }}>🔄</span>
            <span style={{ fontSize: '14px', color: '#495057' }}>Redirect to Specific Node</span>
          </div>
          
          <div
            style={radioButtonStyle}
            onClick={() => setFormData(prev => ({ ...prev, nextAction: 'end' }))}
          >
            <div style={formData.nextAction === 'end' ? selectedRadioStyle : radioCircleStyle}>
              {formData.nextAction === 'end' && <div style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%' }} />}
            </div>
            <span style={{ fontSize: '16px' }}>⏹️</span>
            <span style={{ fontSize: '14px', color: '#495057' }}>End Conversation</span>
          </div>
        </div>

        {/* Target Node Selection (for redirect) */}
        {formData.nextAction === 'redirect' && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
              Target Node
            </label>
            <select
              value={formData.targetNode}
              onChange={e => setFormData(prev => ({ ...prev, targetNode: e.target.value }))}
              style={inputStyle}
            >
              <option value="">Select target node</option>
              <option value="welcome">Welcome Message</option>
              <option value="menu">Main Menu</option>
              <option value="support">Support</option>
              <option value="checkout">Checkout</option>
            </select>
          </div>
        )}

        {/* Transition Message */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Transition Message (Optional)
          </label>
          <textarea
            value={formData.message}
            onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
            placeholder="Enter a message to show during transition..."
            style={textareaStyle}
          />
        </div>

        {/* Delay Setting */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Delay before next action - {formData.delay} sec
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.delay}
              onChange={e => setFormData(prev => ({ ...prev, delay: parseInt(e.target.value) }))}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '14px', color: '#6c757d', minWidth: '50px' }}>
              {formData.delay} sec
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={handleSave}
            style={{ ...buttonStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            💾 Save
          </button>
          <button
            onClick={onClose}
            style={{ ...secondaryButtonStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            ✕ Close
          </button>
        </div>
      </div>
    </div>
  );
} 